// frontend/src/contexts/BusinessEntityContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const BusinessEntityContext = createContext(null);

/**
 * Entity filter values:
 *   'all'      — Show everything (no filter)
 *   'personal' — Only items with no business_entity_id
 *   <number>   — Specific business entity ID
 */
const ENTITY_FILTER_KEY = 'stb_entityFilter';

export function BusinessEntityProvider({ children }) {
  const [entities, setEntities] = useState([]); // List of active business entities
  const [activeFilter, _setActiveFilter] = useState(() => {
    try { return localStorage.getItem(ENTITY_FILTER_KEY) || 'all'; } catch { return 'all'; }
  });
  const [loading, setLoading] = useState(true);

  // Wrap setter to persist to localStorage
  const setActiveFilter = useCallback((val) => {
    _setActiveFilter(val);
    try { localStorage.setItem(ENTITY_FILTER_KEY, String(val)); } catch { /* noop */ }
  }, []);

  const loadEntities = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/business-entities');
      setEntities(res.data.businessEntities || []);
    } catch (err) {
      console.error('Failed to load business entities:', err);
      setEntities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  // Reset stale entity filter (e.g., deleted business) after entities load
  useEffect(() => {
    if (!loading && activeFilter !== 'all' && activeFilter !== 'personal') {
      const exists = entities.some(e => String(e.id) === String(activeFilter));
      if (!exists) setActiveFilter('all');
    }
  }, [entities, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build the query param value for API calls
  const filterParam = activeFilter === 'all' ? undefined : String(activeFilter);

  // Human-readable label for the current filter
  const filterLabel = (() => {
    if (activeFilter === 'all') return 'All';
    if (activeFilter === 'personal') return 'Personal';
    const entity = entities.find(e => e.id === activeFilter);
    return entity?.name || 'Business';
  })();

  const value = {
    entities,
    activeFilter,
    setActiveFilter,
    filterParam,     // Pass as ?businessEntityId= in API calls
    filterLabel,
    loading,
    hasEntities: entities.length > 0,
    reload: loadEntities
  };

  return (
    <BusinessEntityContext.Provider value={value}>
      {children}
    </BusinessEntityContext.Provider>
  );
}

export function useBusinessEntity() {
  const ctx = useContext(BusinessEntityContext);
  if (!ctx) {
    // Return a no-op default so components outside the provider still work
    return {
      entities: [],
      activeFilter: 'all',
      setActiveFilter: () => {},
      filterParam: undefined,
      filterLabel: 'All',
      loading: false,
      hasEntities: false,
      reload: () => {}
    };
  }
  return ctx;
}

export default BusinessEntityContext;
